/// <amd-module name="Wit/SprintsHub/SprintsView/Components/WorkDetailsPane" />
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { format, isGuid } from "VSS/Core/Util/String";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { css, usePageContext, useService } from "VSS/Platform/Layout";
import { Header } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { BoltListDragEvent, IListDropData, IListItemDetails, List, ListDropTargetBehavior, ListItem } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { IBacklogTreeRow } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { MaximumFractionDigits, getNumberInUserFormat } from "Wit/Common/Utilities/FormatUtils";
import { onAssignToItemActivated, onChangeActivityActivated } from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { WorkDetailsSectionType } from "Wit/SprintsHub/Common/Constants";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ICapacityService } from "Wit/SprintsHub/SprintsView/Services/ICapacityService";
import {
    GroupedProgressDataProvider,
    IGroupedProgressDataChange,
    IWorkDetailsService,
    WorkDetailDataProivderTypes
} from "Wit/SprintsHub/SprintsView/Services/WorkDetailsService";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import * as React from "react";
import { PropsWithChildren, useEffect, useState } from "react";

const capacityHelpLink = "https://go.microsoft.com/fwlink/?linkid=2043831&clcid=0x409";

export interface IWorkDetailsPaneProps {
    selectedIteration?: IIterationData;
    teamSettings?: ITeamSettings;
    tasksBacklogLevelWorkItemTypes?: Set<string>;
}

export function WorkDetailsPane(props: IWorkDetailsPaneProps) {
    const workDetailsService = useService<IWorkDetailsService>("IWorkDetailsService");

    useEffect(() => {
        if (props.teamSettings && props.selectedIteration) {
            workDetailsService.loadData(props.teamSettings, props.selectedIteration);
        }
    }, [props.teamSettings, props.selectedIteration]);

    const teamDataProvider = workDetailsService.getDataProvider(WorkDetailDataProivderTypes.Team);

    return (
        <div className="v-scroll-auto" data-elementtype="side-panel-container">
            <Observer data={teamDataProvider.getData()}>
                {(observedProps: { data: IGroupedProgressDataChange[] }) => {
                    let headerDescription: React.ReactNode = Resources.WorkDetailsSubheader;

                    const hasData = observedProps.data.length !== 0;

                    if (!hasData) {
                        headerDescription = (
                            <>
                                <Link href={capacityHelpLink} target="_blank">
                                    {Resources.WorkDetailsZeroDataLink}
                                </Link>{" "}
                                {Resources.WorkDetailsZeroDataText}
                            </>
                        );
                    }

                    return (
                        <>
                            <Header className="padding-bottom-16" title={Resources.WorkDetails} description={headerDescription} />
                            {hasData && (
                                <>
                                    <WorkDetailsSection type="TEAM" dataProvider={teamDataProvider} />
                                    <WorkDetailsSection
                                        type={WorkDetailsSectionType.ACTIVITY}
                                        dataProvider={workDetailsService.getDataProvider(WorkDetailDataProivderTypes.Activity)}
                                        droppable
                                        tasksBacklogLevelWorkItemTypes={props.tasksBacklogLevelWorkItemTypes}
                                    />
                                    <WorkDetailsSection
                                        type={WorkDetailsSectionType.ASSIGNED_TO}
                                        dataProvider={workDetailsService.getDataProvider(WorkDetailDataProivderTypes.AssignedTo)}
                                        droppable
                                    />
                                </>
                            )}
                        </>
                    );
                }}
            </Observer>
        </div>
    );
}

function WorkDetailsCollapsibleSection({ children, title }: PropsWithChildren<{ title: string }>) {
    const [collapsed, setCollapsed] = useState<boolean>(false);

    return (
        <div className="flex-column padding-horizontal-16 padding-vertical-8">
            <div
                className="flex-row flex-grow flex-center padding-bottom-8 justify-space-between cursor-pointer"
                onClick={() => setCollapsed(!collapsed)}
            >
                <div className="title-xs">{title}</div>
                <Icon className="flex-self-end" iconName={collapsed ? "ChevronLeft" : "ChevronDown"} />
            </div>
            {!collapsed && children}
        </div>
    );
}

export type WorkDetailsSectionType = "TEAM" | "ACTIVITY" | "ASSIGNED_TO";

function WorkDetailsSection({
    type,
    dataProvider,
    droppable,
    tasksBacklogLevelWorkItemTypes
}: {
    type: WorkDetailsSectionType;
    dataProvider: GroupedProgressDataProvider;
    droppable?: boolean;
    tasksBacklogLevelWorkItemTypes?: Set<string>;
}) {
    const pageContext = usePageContext();
    const capacityService = useService<ICapacityService>("ICapacityService");
    const backlogContentService = useService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const backlogWorkItemService = useService<IBacklogWorkItemService>("IBacklogWorkItemService");
    const data = dataProvider.getData();
    const useProcessingDialogForLongBacklogOperations = isFeatureFlagEnabled(pageContext, FeatureFlags.UseProcessingDialogForLongBacklogOperationsEnabled, false);
    const activityFieldReferenceName = React.useMemo(
        () => capacityService.getCapacityOptions()?.activityFieldReferenceName ?? "Microsoft.VSTS.Common.Activity",
        [capacityService]
    );

    const behaviors = droppable
        ? [
            new ListDropTargetBehavior<ITreeItemEx<IBacklogTreeRow>>({
                type: "backlog-item",
                onDrop: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData) => onDrop(event, dropData),
                onDragOver: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData) => onDragOver(event, dropData),
                allowedTypes: ["backlog-item"],
                isTree: true
            })
        ]
        : undefined;

    const onDragOver = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData): void => {
        if (type === WorkDetailsSectionType.ASSIGNED_TO || !tasksBacklogLevelWorkItemTypes) {
            return;
        }
        const row = event.detail.dataTransfer.data.underlyingItem.data.value;
        const workItemType = row.coreColumnMap[CoreFieldRefNames.WorkItemType] as string;
        const isTaskOrRequirement = tasksBacklogLevelWorkItemTypes.has(workItemType);
        if (!isTaskOrRequirement) {
            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
        }
    };

    const onDrop = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData): void => {
        const groupedData = data.value[dropData.index];
        if (type === WorkDetailsSectionType.ASSIGNED_TO) {
            const items = backlogContentService.getSelectedItems(event.detail.dataTransfer.data.underlyingItem?.data);
            const getSelectedItems = (): INamedFieldDataDictionary[] => items.map(item => ({ ...item.value.coreColumnMap }));

            onAssignToItemActivated(
                pageContext,
                getSelectedItems,
                groupedData.workItemIdentityRef,
                workItems => backlogWorkItemService.updateWorkItems(workItems, items),
                undefined,
                useProcessingDialogForLongBacklogOperations,
                backlogContentService.getTreeSelection()
            );
        } else if (type === WorkDetailsSectionType.ACTIVITY) {
            const activityId = groupedData.id === Resources.Unassigned ? undefined : groupedData.id;

            const items = backlogContentService.getSelectedItems(event.detail.dataTransfer.data.underlyingItem?.data);
            const getSelectedItems = (): INamedFieldDataDictionary[] => items.map(item => ({ ...item.value.coreColumnMap }));
            const selection = backlogContentService.getTreeSelection();
            onChangeActivityActivated(
                pageContext,
                getSelectedItems,
                activityId,
                activityFieldReferenceName,
                workItems => backlogWorkItemService.updateWorkItems(workItems, items),
                useProcessingDialogForLongBacklogOperations,
                selection
            );
        }
    };

    const groups = (
        <Observer data={data}>
            {(observedProps: { data: IGroupedProgressDataChange[] }) => {
                const maxTotal = observedProps.data.reduce((previousMax, group) => Math.max(previousMax, group.current, group.total), 0);
                const itemProvider = new ArrayItemProvider(observedProps.data);

                return (
                    <List
                        className="work-details-pane-list"
                        behaviors={behaviors}
                        itemProvider={itemProvider}
                        renderRow={(rowIndex: number, group: IGroupedProgressDataChange, details: IListItemDetails<IGroupedProgressDataChange>) => {
                            let label: JSX.Element = <div>{group.text}</div>;
                            if (isGuid(group.id)) {
                                const imageUrl = getIdentityImageUrl(pageContext, group.id);

                                label = (
                                    <div className="flex-row flex-center padding-bottom-4">
                                        <Persona
                                            className="padding-right-4"
                                            identity={{ displayName: group.text, id: group.id, imageUrl: imageUrl }}
                                            personaProvider={new PersonaDetailsProvider(pageContext)}
                                            size={PersonaSize.size32}
                                        />
                                        {group.text}
                                    </div>
                                );
                            }

                            const currentValue = getNumberInUserFormat(group.current, MaximumFractionDigits);
                            const totalValue = getNumberInUserFormat(group.total, MaximumFractionDigits);

                            const additionalDataAttributes = {
                                "data-workdetailspanetype": type,
                                ...(type === WorkDetailsSectionType.ACTIVITY ? { "data-activityid": group.id } : {}),
                                ...(type === WorkDetailsSectionType.ASSIGNED_TO
                                    ? { "data-identityid": group.id, "data-workitemidentityref": JSON.stringify(group.workItemIdentityRef) }
                                    : {})
                            };
                            return (
                                <ListItem key={rowIndex} index={rowIndex} details={details}>
                                    <div
                                        className="flex-column padding-bottom-8 padding-right-4 work-details-pane-list-item"
                                        {...additionalDataAttributes}
                                    >
                                        {label}

                                        {group.total ? (
                                            <WorkDetailsProgressBar current={group.current} maxTotal={maxTotal} total={group.total} />
                                        ) : null}

                                        <div>
                                            {format(
                                                capacityService.getCapacityOptions()?.remainingWorkSuffixFormat ?? "{0}",
                                                format(group.total ? Resources.ProgressRatioLabel : "{0}", currentValue, totalValue)
                                            )}
                                        </div>
                                    </div>
                                </ListItem>
                            );
                        }}
                    />
                );
            }}
        </Observer>
    );

    return <WorkDetailsCollapsibleSection title={dataProvider.getTitle()}>{groups}</WorkDetailsCollapsibleSection>;
}

function WorkDetailsProgressBar({ current, maxTotal, total }: { current: number; maxTotal: number; total: number }) {
    return (
        <div className="work-details-progress-bar-container relative" style={{ width: `${(total / maxTotal) * 100}%` }}>
            <div className="work-details-total-mark absolute" />
            <div
                className={css("work-details-progress-bar-fill", current > total && "over-capacity")}
                style={{ width: `${(current / total) * 100}%` }}
            />
        </div>
    );
}
